import { cn } from "@wags/ui/ui-utils";
import type { Ref } from "react";
import WagsLogo from "@/assets/icons/wags-logo.svg";
import { BOTTOM_NAV_RESERVED_HEIGHT } from "@/lib/ui/bottomNav";
import { FooterWordmark } from "./footer-wordmark/FooterWordmark";

const NAV_COLUMNS = [
    {
        heading: "Explore",
        links: [
            { label: "Find a pet", href: "/find" },
            { label: "Most-loved puppies", href: "/explore" },
        ],
    },
    {
        heading: "Resources",
        links: [
            { label: "FAQ", href: "/faq" },
            { label: "Take the quiz", href: "/filter-quiz" },
            {
                label: "Claim your page",
                href: "/faq#for-breeders",
            },
        ],
    },
    {
        heading: "Company",
        links: [
            { label: "About us", href: "/ethos" },
            { label: "Our values", href: "/values" },
            { label: "Contact us", href: "mailto:support@wags.com" },
        ],
    },
] as const;

const FOOTER_LINK =
    "transition-colors focus-visible:outline-none focus-visible:underline focus-visible:underline-offset-4";

export function Footer({
    ref,
    surfaceClassName = "bg-maltese-white",
    reserveBottomNav = false,
}: {
    ref?: Ref<HTMLElement>;
    surfaceClassName?: string;
    reserveBottomNav?: boolean;
}) {
    // eslint-disable-next-line react-doctor/rendering-hydration-mismatch-time -- the copyright year is fine to differ across a New Year hydration boundary.
    const year = new Date().getFullYear();

    return (
        <div className="relative">
            <div
                className={cn(
                    "pointer-events-none absolute inset-x-0 top-0 z-10 h-10 rounded-b-[2.25rem] md:h-12 md:rounded-b-[2.75rem]",
                    surfaceClassName,
                )}
            />
            <footer
                ref={ref}
                className="relative z-0 overflow-hidden bg-doberman-black pt-20 md:pt-24"
            >
                <div className="page-container relative z-10">
                    <div className="grid grid-cols-1 gap-9 md:grid-cols-[1fr_auto_auto_auto] md:gap-x-12 lg:gap-x-16">
                        <div className="flex flex-col gap-px">
                            <a
                                href="/find"
                                aria-label="Wags home"
                                className="wags-logo-link w-fit text-labrador-yellow md:-mt-3"
                            >
                                <WagsLogo className="h-11 w-auto md:h-[3.25rem]" />
                            </a>
                            <a
                                href="mailto:support@wags.com"
                                className={cn(
                                    "w-fit text-body-small text-maltese-white/70 hover:text-maltese-white",
                                    FOOTER_LINK,
                                )}
                            >
                                support@wags.com
                            </a>
                        </div>

                        {NAV_COLUMNS.map((col) => (
                            <div
                                key={col.heading}
                                className="flex flex-col gap-2"
                            >
                                <p className="text-body-small font-medium text-labrador-yellow">
                                    {col.heading}
                                </p>
                                <nav className="flex flex-col gap-1.5 text-body-small text-maltese-white/70">
                                    {col.links.map((link) => (
                                        <a
                                            key={link.label}
                                            href={link.href}
                                            className={cn(
                                                FOOTER_LINK,
                                                "hover:text-maltese-white",
                                            )}
                                        >
                                            {link.label}
                                        </a>
                                    ))}
                                </nav>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 flex flex-col gap-3 text-caption text-maltese-white/40 md:mt-16 md:flex-row md:items-center md:justify-between">
                        <p>wags, Inc. {year}</p>
                        <div className="flex items-center gap-5 md:translate-y-[2px]">
                            <a
                                href="/terms"
                                className={cn(
                                    FOOTER_LINK,
                                    "hover:text-maltese-white/60",
                                )}
                            >
                                Terms of Service
                            </a>
                            <a
                                href="/privacy"
                                className={cn(
                                    FOOTER_LINK,
                                    "hover:text-maltese-white/60",
                                )}
                            >
                                Privacy Policy
                            </a>
                        </div>
                    </div>
                </div>
                <FooterWordmark />
                {reserveBottomNav && (
                    <div
                        className={cn(BOTTOM_NAV_RESERVED_HEIGHT, "md:hidden")}
                    />
                )}
            </footer>
        </div>
    );
}
